import { Input } from '@heidi/design-system/input';
import { Textarea } from '@heidi/design-system/textarea';
import { TypographyP2 } from '@heidi/design-system/typography';
import { Calendar } from 'lucide-react';
import { useId, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { InlineApprovalActions } from './inline-approval-actions';
import { InlineApprovalCardShell } from './inline-approval-card-shell';
import { LabelledContextPanel } from './labelled-context-panel';
import {
  Field,
  useNangoApprovalSubmit,
  type NangoConnectorApprovalCardProps,
} from './nango-approval-submit';
import {
  buildCalendarModifiedInput,
  formatAddressList,
  parseAddressListInput,
  readCalendarApprovalFields,
  resolveDatetimeForApprove,
  toDatetimeLocalValue,
  type CalendarApprovalFields,
} from './nango-connector-approval';

export function CalendarApprovalCard({
  approval,
  onResolve,
}: NangoConnectorApprovalCardProps) {
  const intl = useIntl();
  const fieldId = useId();
  const { pendingVerdict, isBusy, submit } = useNangoApprovalSubmit(
    approval.approval_id,
    onResolve
  );
  const initial = useMemo(
    () => readCalendarApprovalFields(approval.tool_name, approval.tool_input),
    [approval.tool_input, approval.tool_name]
  );
  const [subject, setSubject] = useState(() => initial?.subject ?? '');
  const [start, setStart] = useState(() => toDatetimeLocalValue(initial?.start));
  const [end, setEnd] = useState(() => toDatetimeLocalValue(initial?.end));
  const [description, setDescription] = useState(() => initial?.description ?? '');
  const [attendees, setAttendees] = useState(() => formatAddressList(initial?.attendees));

  if (!initial) return null;

  const isDelete = initial.toolName === 'integrations.calendar.delete';
  const canApprove = isDelete
    ? Boolean(initial.eventId)
    : initial.toolName === 'integrations.calendar.update'
      ? Boolean(initial.eventId)
      : subject.trim().length > 0 && Boolean(start) && Boolean(end);

  const currentFields = (): CalendarApprovalFields => ({
    ...initial,
    subject,
    start: resolveDatetimeForApprove(initial.start, start),
    end: resolveDatetimeForApprove(initial.end, end),
    description,
    attendees: parseAddressListInput(attendees),
  });

  const title =
    initial.toolName === 'integrations.calendar.delete'
      ? intl.formatMessage({
          defaultMessage: 'Delete calendar event',
          id: 'K5pU3EOdnG',
        })
      : initial.toolName === 'integrations.calendar.update'
        ? intl.formatMessage({
            defaultMessage: 'Update calendar event',
            id: 'c8F3rZ89Kx',
          })
        : intl.formatMessage({
            defaultMessage: 'Create calendar event',
            id: 'jsLlTVyLr6',
          });

  return (
    <InlineApprovalCardShell
      Icon={Calendar}
      title={title}
      subtitle={
        isDelete
          ? intl.formatMessage({
              defaultMessage: 'This may notify attendees',
              id: 'aBU8EJINOY',
            })
          : intl.formatMessage({
              defaultMessage: 'Review and edit before it is saved',
              id: 'giZiped0Ib',
            })
      }
      urgent={!isBusy}
      data-testid={`nango-calendar-approval-${approval.approval_id}`}
    >
      <div className="flex flex-col gap-3">
        {initial.eventId ? (
          <LabelledContextPanel
            label={intl.formatMessage({
              defaultMessage: 'Event',
              id: 'm1czzYTiJ/',
            })}
          >
            <TypographyP2 className="break-all font-mono text-sm text-text-secondary">
              {initial.eventId}
            </TypographyP2>
          </LabelledContextPanel>
        ) : null}
        {isDelete ? null : (
          <>
            <Field
              id={`${fieldId}-title`}
              label={intl.formatMessage({ defaultMessage: 'Title', id: '9a9+wwWy4u' })}
            >
              <Input
                id={`${fieldId}-title`}
                value={subject}
                onChange={(event) => setSubject(event.target.value)}
                disabled={isBusy}
                data-testid="nango-calendar-approval-title"
              />
            </Field>
            <Field
              id={`${fieldId}-start`}
              label={intl.formatMessage({ defaultMessage: 'Starts', id: '9anxhqOyRo' })}
            >
              <Input
                id={`${fieldId}-start`}
                type="datetime-local"
                value={start}
                onChange={(event) => setStart(event.target.value)}
                disabled={isBusy}
                data-testid="nango-calendar-approval-start"
              />
            </Field>
            <Field
              id={`${fieldId}-end`}
              label={intl.formatMessage({ defaultMessage: 'Ends', id: '04xGLEkMMo' })}
            >
              <Input
                id={`${fieldId}-end`}
                type="datetime-local"
                value={end}
                onChange={(event) => setEnd(event.target.value)}
                disabled={isBusy}
                data-testid="nango-calendar-approval-end"
              />
            </Field>
            <Field
              id={`${fieldId}-attendees`}
              label={intl.formatMessage({
                defaultMessage: 'Attendees',
                id: '2ow9UmwYzF',
              })}
            >
              <Input
                id={`${fieldId}-attendees`}
                value={attendees}
                onChange={(event) => setAttendees(event.target.value)}
                disabled={isBusy}
                data-testid="nango-calendar-approval-attendees"
              />
            </Field>
            <Field
              id={`${fieldId}-description`}
              label={intl.formatMessage({
                defaultMessage: 'Description',
                id: 'Q8Qw5BZ1VA',
              })}
            >
              <Textarea
                id={`${fieldId}-description`}
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                minRows={2}
                maxRows={8}
                autoResize
                disabled={isBusy}
                data-testid="nango-calendar-approval-description"
              />
            </Field>
          </>
        )}
        <InlineApprovalActions
          onApprove={() =>
            submit(
              'approve',
              buildCalendarModifiedInput(approval.tool_input, currentFields())
            )
          }
          onDeny={() => submit('deny')}
          pendingVerdict={pendingVerdict}
          disabled={isBusy}
          approveDisabled={!canApprove}
          approveLabel={
            isDelete
              ? intl.formatMessage({ defaultMessage: 'Delete', id: 'K3r6DQW7h+' })
              : undefined
          }
        />
      </div>
    </InlineApprovalCardShell>
  );
}
